import React, { FormEvent, useCallback, useEffect, useState } from 'react';
import { Button, Chip } from '@mui/material';
import * as yup from 'yup';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Appbar from 'components/appbar/Appbar';
import AddressFields from 'components/address/AddressFields';
import ConfirmDialog from 'components/confirm-dialog/ConfirmDialog';
import InsideLoading from 'components/loading/InsideLoading';
import InsideSaving from 'components/loading/InsideSaving';
import NoData from 'components/no-data/NoData';
import {
  ContentPad,
  PageShell,
  RecordFooter,
  RecordHeader,
  RecordNote,
  StyledForm,
  WorkArea,
} from 'components/record-shell/RecordShell';
import { AddressErrors, emptyAddress, validateAddress } from 'helpers/addressValidation';
import { normalizeDate } from 'helpers/parsePtBRDate';
import { api } from 'services/api';
import history from 'services/history';
import { Bank } from 'types/bank';
import { Doctor, DoctorAddress, DoctorBank } from 'types/doctor';
import { Specialty } from 'types/specialty';
import DoctorRecordContent from '../../record/DoctorRecordContent';
import DoctorBanks from '../DoctorBanks';
import { buildDoctorPayload } from '../doctorPayload';
import DoctorForm from '../form/DoctorForm';
import DoctorTab from '../tab/DoctorTab';
import { useDoctorValidation } from '../validation/useDoctorValidation';

// Campo com erro de validação -> aba onde ele está (todos os campos do formulário ficam em "Dados do médico")
const FORM_TAB = 'main';

const EditDoctor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [banks, setBanks] = useState<Bank[]>([]);
  const [validation, setValidation, validate] = useDoctorValidation();
  // a ficha é a primeira coisa que se vê ao abrir o médico
  const [tab, setTab] = useState('record');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [loadingSpecialties, setLoadingSpecialties] = useState(false);
  const [specialties, setSpecialties] = useState<Specialty[]>([]);
  const [doctor, setDoctor] = useState<Doctor | null>(null);
  const [savedName, setSavedName] = useState('');
  const [savedActive, setSavedActive] = useState(true);
  const [doctorAddress, setDoctorAddress] = useState<DoctorAddress | null>(null);
  const [addressErrors, setAddressErrors] = useState<AddressErrors>({});
  const [recordReloadKey, setRecordReloadKey] = useState(0);

  const loadDoctor = useCallback(() => {
    if (!id) return Promise.resolve();

    return api
      .get(`/doctors/${id}`)
      .then(response => {
        const doctorData = response.data;
        setDoctor({ ...doctorData, birth_date: doctorData.birth_date ? normalizeDate(doctorData.birth_date) : null });
        setDoctorAddress(doctorData.address ?? { ...emptyAddress, doctor_id: doctorData.id });
        setSavedName(doctorData.name);
        setSavedActive(!!doctorData.active);
      })
      .catch(err => {
        console.error(err);
        toast.error('Não foi possível carregar os dados do médico');
        history.push('/doctors');
      });
  }, [id]);

  useEffect(() => {
    setLoading(true);
    loadDoctor().finally(() => setLoading(false));
  }, [loadDoctor]);

  useEffect(() => {
    fetch('https://brasilapi.com.br/api/banks/v1')
      .then(response => response.json())
      .then(data => setBanks(data.filter((item: Bank) => !!item.code)))
      .catch(error => console.error('Error fetching bank data:', error));
  }, []);

  useEffect(() => {
    setLoadingSpecialties(true);

    api
      .get('/specialties/all')
      .then(response => setSpecialties(response.data))
      .catch(() => toast.error('Não foi possível carregar as especialidades'))
      .finally(() => setLoadingSpecialties(false));
  }, []);

  // a validação guarda o erro do campo; abre a aba onde ele está
  useEffect(() => {
    if (Object.keys(validation).length > 0) setTab(FORM_TAB);
  }, [validation]);

  function goBack() {
    history.push('/doctors');
  }

  function handleChange<K extends keyof Doctor>(index: K, value: Doctor[K]) {
    setDoctor(prev => (prev ? { ...prev, [index]: value } : null));
  }

  function handleAddressChange(address: DoctorAddress) {
    setDoctorAddress(address);
    setAddressErrors({});
  }

  function handleSubmit() {
    if (!doctor) return;

    setSaving(true);

    const data = {
      ...buildDoctorPayload(doctor, doctorAddress),
      banks: doctor.banks.map(({ created_at, updated_at, ...rest }) => rest),
    };

    api
      .put(`/doctors/${doctor.id}`, data)
      .then(() => loadDoctor())
      .then(() => {
        toast.success('Médico atualizado');
        setRecordReloadKey(key => key + 1);
        setTab('record');
      })
      .catch(err => {
        toast.error(err.response?.data?.error || 'Não foi possível atualizar o médico');
      })
      .finally(() => setSaving(false));
  }

  function handleValidation(e?: FormEvent<HTMLFormElement>) {
    e?.preventDefault();
    if (!doctor) return;

    validate(doctor)
      .then(() => {
        const errors = validateAddress(doctorAddress);
        setAddressErrors(errors);

        if (Object.keys(errors).length > 0) {
          setTab('address');
          toast.error('Corrija o endereço para salvar');
          return;
        }

        handleSubmit();
        setValidation({});
      })
      .catch((err: yup.ValidationError) => {
        if (err instanceof yup.ValidationError) {
          setValidation({
            [err.path as string]: err.message,
          });
        }
      });
  }

  function handleDeleteDoctor() {
    if (!doctor) return;

    setLoading(true);

    api
      .delete(`/doctors/${doctor.id}`)
      .then(() => {
        toast.success('Médico excluído com sucesso!');
        history.push('/doctors');
      })
      .catch(err => {
        toast.error(err.response?.data?.error || 'Não foi possível excluir o médico');
      })
      .finally(() => {
        setLoading(false);
        setConfirmDialogOpen(false);
      });
  }

  // contas bancárias são gravadas na hora, sem passar pelo Salvar
  function updateBanks(banksPayload: DoctorBank[], extra: object, failure: string) {
    if (!doctor) return;

    api
      .put(`/doctors/${doctor.id}`, { ...doctor, ...extra, banks: banksPayload })
      .then(response => {
        toast.success('Dados bancários salvos');
        setDoctor(state => (state ? { ...state, banks: response.data.banks } : null));
      })
      .catch(err => {
        toast.error(err.response?.data?.error || failure);
      });
  }

  function handleChangeAddBank(bank: DoctorBank) {
    if (!doctor) return;

    updateBanks([bank], buildDoctorPayload(doctor, doctorAddress), 'Não foi possível adicionar a conta bancária');
  }

  function handleDeleteBank(bank: DoctorBank) {
    if (!doctor) return;

    updateBanks(
      doctor.banks.map(b => (b.id === bank.id ? { ...b, active: 0 } : b)),
      {},
      'Não foi possível excluir a conta bancária',
    );
  }

  const isEditing = tab === FORM_TAB || tab === 'address';

  return (
    <>
      <Appbar title="Médicos" backAction={goBack} />

      {saving && <InsideSaving />}

      {confirmDialogOpen && (
        <ConfirmDialog
          confirmLabel="Excluir"
          destructive
          handleNoClick={() => setConfirmDialogOpen(false)}
          handleYesClick={handleDeleteDoctor}
          title="Deseja excluir esse médico?"
        />
      )}

      <PageShell>
        <RecordHeader title={savedName || 'Médico'} active={savedActive} backAction={goBack}>
          {doctor?.crm && <Chip size="small" variant="outlined" label={`CRM ${doctor.crm}`} />}
        </RecordHeader>

        <WorkArea>
          <DoctorTab showRecord onChange={newTab => setTab(newTab)} tab={tab} banksCount={doctor?.banks.length} />
          <ContentPad>
            {loading ? (
              <InsideLoading />
            ) : !doctor ? (
              <NoData message="Não foi possível carregar os dados do médico" />
            ) : tab === 'record' ? (
              <DoctorRecordContent id={id} reloadKey={recordReloadKey} />
            ) : tab === 'banks' ? (
              <>
                <DoctorBanks
                  handleDeleteBank={handleDeleteBank}
                  banks={banks}
                  doctor={doctor}
                  handleChangeAddBank={handleChangeAddBank}
                />
                <RecordNote>Adicionar, editar e excluir conta salva na hora.</RecordNote>
              </>
            ) : (
              <StyledForm onSubmit={handleValidation}>
                {tab === 'address' ? (
                  <AddressFields address={doctorAddress} onChange={handleAddressChange} errors={addressErrors} />
                ) : (
                  <DoctorForm
                    handleChange={handleChange}
                    doctor={doctor}
                    validation={validation}
                    specialties={specialties}
                    loadingSpecialties={loadingSpecialties}
                  />
                )}
                <button type="submit" style={{ display: 'none' }}></button>
              </StyledForm>
            )}
          </ContentPad>
        </WorkArea>

        {isEditing && !loading && doctor && (
          <RecordFooter sx={{ justifyContent: 'space-between' }}>
            <Button color="error" size="small" disabled={saving} onClick={() => setConfirmDialogOpen(true)}>
              Excluir médico
            </Button>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button variant="contained" size="small" disabled={saving} onClick={() => handleValidation()}>
                Salvar alterações
              </Button>
            </div>
          </RecordFooter>
        )}
      </PageShell>
    </>
  );
};

export default EditDoctor;
